/**
 * Branded column types for type-safe timescale handling.
 *
 * This module provides compile-time type safety for column timescales,
 * preventing bugs where columns at one granularity are passed to code
 * expecting a different granularity (e.g., passing a day column to
 * weekly row plugin binding).
 */

import { PartialDate, TimezoneRegion } from "@tento-chrono";
import type { Timescale } from "@tento-lona/sheets";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { DebugAttributes } from "@tento-ui/components/sheet/debug-attributes";

/**
 * A column that carries its timescale at the type level.
 * The _timescale field is a phantom type - it exists only for type checking
 * and has no runtime representation.
 *
 * Use createTypedColumn() to create instances and assertColumnTimescale()
 * to validate existing columns.
 */
export interface TypedColumn<T extends Timescale>
  extends ColumnIndex.Key.Resolved.Default {
  /**
   * Phantom type field - does not exist at runtime.
   * Used only for compile-time type discrimination.
   */
  readonly _timescale?: T;
}

/**
 * Create a column at a specific timescale from a PartialDate.
 * This is the primary way to create properly typed columns for row binding.
 *
 * @example
 * // Create a week-level column for binding weekly row data
 * const weekColumn = createTypedColumn("week", weekPd, referenceColumn);
 *
 * // The type system now knows this column is at week granularity
 * bindWeeklyRow(weekColumn); // Passes type checking
 * bindDailyRow(weekColumn);  // Type error if dailyRow expects TypedColumn<"day">
 *
 * @param timescale The timescale for the new column
 * @param period The PartialDate for the column
 * @param referenceColumn A reference column to inherit timezone and offset from
 */
function createTypedColumn<T extends Timescale>(
  timescale: T,
  period: PartialDate,
  referenceColumn: ColumnIndex.Key.Resolved.Default,
): TypedColumn<T> {
  // Validate that the period matches the expected timescale
  if (DebugAttributes.enabled) {
    const actualTimescale = period.type;
    if (actualTimescale !== timescale) {
      DebugAttributes.trace("column-typed/mismatch", {
        expected: timescale,
        actual: actualTimescale,
        period: period.toString(),
      });
    }
  }

  return new ColumnIndex.Key.Resolved.Default(
    period,
    referenceColumn.tz,
    referenceColumn.offset,
    null,
    null,
  ) as TypedColumn<T>;
}

/**
 * Create a typed column from a row timescale and visible period.
 * This is the canonical way to create a column for static child rendering,
 * ensuring the column timescale matches the row timescale.
 *
 * @param rowTimescale The row's timescale (must not be "custom")
 * @param period The period at the row's timescale
 * @param tz The timezone for the column
 * @param offset The column offset
 */
function createRowTimescaleColumn<T extends Timescale>(
  rowTimescale: T,
  period: PartialDate,
  tz: TimezoneRegion,
  offset: number,
): TypedColumn<T> {
  if (DebugAttributes.enabled) {
    // Validate that period type matches row timescale
    if (period.type !== rowTimescale) {
      DebugAttributes.trace("column-typed/row-mismatch", {
        rowTimescale,
        periodType: period.type,
        period: period.toString(),
      });
    }
  }

  return new ColumnIndex.Key.Resolved.Default(
    period,
    tz,
    offset,
    null,
    null,
  ) as TypedColumn<T>;
}

/**
 * Assert that a column matches an expected timescale.
 * In debug mode, logs mismatches for investigation.
 * Returns the column cast to the expected type for type narrowing.
 *
 * @example
 * // At a binding point, assert the expected timescale
 * const weekCol = assertColumnTimescale(column, "week", "updateStaticChildSpans");
 * // weekCol is now TypedColumn<"week">
 *
 * @param column The column to validate
 * @param expectedTimescale The expected timescale
 * @param context A context string for debug logging (function name, location)
 * @returns The column cast to TypedColumn<T>
 */
function assertColumnTimescale<T extends Timescale>(
  column: ColumnIndex.Key.Resolved.Default,
  expectedTimescale: T,
  context: string,
): TypedColumn<T> {
  if (DebugAttributes.enabled) {
    const actualTimescale = column.pd.type;
    if (actualTimescale !== expectedTimescale) {
      DebugAttributes.trace("column-typed/assertion-failed", {
        context,
        expected: expectedTimescale,
        actual: actualTimescale,
        columnPd: column.pd.toString(),
        columnOffset: column.offset,
      });
    }
  }

  return column as TypedColumn<T>;
}

/**
 * Type guard to check if a column is at a specific timescale.
 * Unlike assertColumnTimescale, this returns a boolean and doesn't cast.
 *
 * @param column The column to check
 * @param timescale The timescale to check against
 */
function isColumnTimescale<T extends Timescale>(
  column: ColumnIndex.Key.Resolved.Default,
  timescale: T,
): column is TypedColumn<T> {
  return column.pd.type === timescale;
}

/**
 * Get the timescale of a column.
 * Returns the PartialDate type, which is its `Timescale`.
 */
function getColumnTimescale(
  column: ColumnIndex.Key.Resolved.Default,
): Timescale {
  return column.pd.type;
}

// ── TypedColumn namespace ────────────────────────────────────────

const $createTypedColumn = createTypedColumn;
const $createRowTimescaleColumn = createRowTimescaleColumn;
const $assertColumnTimescale = assertColumnTimescale;
const $isColumnTimescale = isColumnTimescale;
const $getColumnTimescale = getColumnTimescale;

export namespace TypedColumn {
  export const make = $createTypedColumn;
  export const makeRowTimescale = $createRowTimescaleColumn;
  export const assertTimescale = $assertColumnTimescale;
  export const isTimescale = $isColumnTimescale;
  export const timescaleOf = $getColumnTimescale;
}
